<script lang="ts">
  interface Response {
    id: string;
    respondent_badge: string;
    respondent_name: string | null;
    threads_username: string;
    email: string | null;
    computed_tier: string;
    tier_label: string;
    computed_score: number;
    interests: string[];
    submitted_at: string | null;
    supersedes_response_id: string | null;
  }

  interface Props {
    survey: { id: string; slug: string; title: string };
    responses: Response[];
    tier_counts: Record<string, number>;
    total: number;
  }

  let { survey, responses, tier_counts, total }: Props = $props();

  const tierColors: Record<string, string> = {
    yellow: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    green:  'bg-green-100 text-green-800 border-green-300',
    blue:   'bg-blue-100 text-blue-800 border-blue-300'
  };

  const tierHints: Record<string, string> = {
    yellow: '복붙·약간 수정 단계 (입문)',
    green:  '기획·구상 단계, 완성물 없음',
    blue:   '배포·작업물 있음 (경험자)'
  };

  // ===== Filters =====
  let activeTier = $state<'all' | 'yellow' | 'green' | 'blue'>('all');

  // Column-level filters
  let fName = $state('');
  let fThreads = $state('');
  let fInterests = $state('');
  let fBadge = $state('');
  let fScoreMin = $state<number | null>(null);
  let fScoreMax = $state<number | null>(null);
  let fDateFrom = $state(''); // yyyy-mm-dd
  let fDateTo = $state('');   // yyyy-mm-dd

  // Sorting
  type SortKey = 'name' | 'threads' | 'tier' | 'score' | 'submitted_at' | 'badge' | null;
  let sortKey = $state<SortKey>('submitted_at');
  let sortDir = $state<'asc' | 'desc'>('desc');

  function toggleSort(k: SortKey) {
    if (!k) return;
    if (sortKey === k) {
      sortDir = sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      sortKey = k;
      sortDir = k === 'score' || k === 'submitted_at' ? 'desc' : 'asc';
    }
  }

  function sortIcon(k: SortKey): string {
    if (sortKey !== k) return '↕';
    return sortDir === 'asc' ? '↑' : '↓';
  }

  function clearFilters() {
    activeTier = 'all';
    fName = '';
    fThreads = '';
    fInterests = '';
    fBadge = '';
    fScoreMin = null;
    fScoreMax = null;
    fDateFrom = '';
    fDateTo = '';
  }

  let filtered = $derived.by(() => {
    let rows = responses;

    if (activeTier !== 'all') {
      rows = rows.filter(r => r.computed_tier === activeTier);
    }
    if (fName.trim()) {
      const q = fName.trim().toLowerCase();
      rows = rows.filter(r => (r.respondent_name ?? '').toLowerCase().includes(q));
    }
    if (fThreads.trim()) {
      const q = fThreads.trim().toLowerCase().replace(/^@/, '');
      rows = rows.filter(r => (r.threads_username ?? '').toLowerCase().includes(q));
    }
    if (fInterests.trim()) {
      const q = fInterests.trim().toLowerCase();
      rows = rows.filter(r =>
        (r.interests ?? []).some(i => i.toLowerCase().includes(q))
      );
    }
    if (fBadge.trim()) {
      const q = fBadge.trim().toLowerCase();
      rows = rows.filter(r => r.respondent_badge.toLowerCase().includes(q));
    }
    if (fScoreMin !== null) rows = rows.filter(r => r.computed_score >= (fScoreMin as number));
    if (fScoreMax !== null) rows = rows.filter(r => r.computed_score <= (fScoreMax as number));
    if (fDateFrom) {
      const t = new Date(fDateFrom).getTime();
      rows = rows.filter(r => r.submitted_at && new Date(r.submitted_at).getTime() >= t);
    }
    if (fDateTo) {
      const t = new Date(fDateTo + 'T23:59:59').getTime();
      rows = rows.filter(r => r.submitted_at && new Date(r.submitted_at).getTime() <= t);
    }

    if (sortKey) {
      const k = sortKey;
      const dir = sortDir === 'asc' ? 1 : -1;
      const cmp = (a: any, b: any) => {
        if (a == null && b == null) return 0;
        if (a == null) return 1;
        if (b == null) return -1;
        if (typeof a === 'number' && typeof b === 'number') return (a - b) * dir;
        return String(a).localeCompare(String(b), 'ko') * dir;
      };
      rows = [...rows].sort((a, b) => {
        switch (k) {
          case 'name': return cmp(a.respondent_name, b.respondent_name);
          case 'threads': return cmp(a.threads_username, b.threads_username);
          case 'tier': return cmp(a.computed_tier, b.computed_tier);
          case 'score': return cmp(a.computed_score, b.computed_score);
          case 'submitted_at': return cmp(a.submitted_at, b.submitted_at);
          case 'badge': return cmp(a.respondent_badge, b.respondent_badge);
          default: return 0;
        }
      });
    }
    return rows;
  });

  let activeFilterCount = $derived(
    (activeTier !== 'all' ? 1 : 0) +
    (fName ? 1 : 0) + (fThreads ? 1 : 0) + (fInterests ? 1 : 0) + (fBadge ? 1 : 0) +
    (fScoreMin !== null ? 1 : 0) + (fScoreMax !== null ? 1 : 0) +
    (fDateFrom ? 1 : 0) + (fDateTo ? 1 : 0)
  );

  function exportCsv() {
    window.location.href = `/admin/community/surveys/${survey.slug}/responses/export_csv`;
  }
</script>

<div class="p-6 space-y-6">
  <header class="flex items-center justify-between">
    <div>
      <a href="/admin/community/surveys" class="text-sm text-gray-600 hover:underline">← 설문 목록</a>
      <h1 class="text-2xl font-bold mt-1">{survey.title} 응답</h1>
    </div>
    <button onclick={exportCsv} class="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">
      📥 CSV
    </button>
  </header>

  <!-- 안내 -->
  <div class="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900 leading-relaxed">
    <strong>⚠️ 1차 자동 분류 (참고용):</strong>
    아래 Yellow / Green / Blue 는 응답 점수 기반 <strong>자동 추천 tier</strong>입니다.
    실제 반 배정은 각 응답 내용을 확인 후 수동으로 결정해주세요.
  </div>

  <!-- 요약 + tier 필터 버튼 -->
  <div class="grid grid-cols-4 gap-4">
    <button type="button"
            onclick={() => activeTier = 'all'}
            class="p-4 border rounded bg-white text-left transition
                   {activeTier === 'all' ? 'ring-2 ring-blue-500' : 'hover:bg-gray-50'}">
      <div class="text-sm text-gray-500">전체 응답</div>
      <div class="text-2xl font-bold">{total}</div>
    </button>
    {#each ['yellow', 'green', 'blue'] as t}
      <button type="button"
              onclick={() => activeTier = t as 'yellow' | 'green' | 'blue'}
              class="p-4 border rounded text-left transition {tierColors[t]}
                     {activeTier === t ? 'ring-2 ring-offset-1 ring-blue-500' : 'opacity-90 hover:opacity-100'}">
        <div class="text-xs font-medium uppercase tracking-wide">{t}</div>
        <div class="text-2xl font-bold mt-0.5">{tier_counts?.[t] ?? 0}</div>
        <div class="text-[11px] mt-1 opacity-75">{tierHints[t]}</div>
      </button>
    {/each}
  </div>

  <!-- 필터 상태 표시줄 -->
  <div class="flex items-center justify-between text-sm">
    <div class="text-gray-600">
      <span class="font-medium">표시:</span> {filtered.length}건 / 전체 {total}건
      {#if activeFilterCount > 0}
        <span class="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs">필터 {activeFilterCount}개 적용중</span>
      {/if}
    </div>
    {#if activeFilterCount > 0}
      <button onclick={clearFilters}
              class="text-xs text-red-600 hover:underline">필터 모두 지우기</button>
    {/if}
  </div>

  <!-- 응답 목록 -->
  <div class="bg-white border rounded overflow-hidden">
    <table class="w-full">
      <thead class="bg-gray-50 text-left text-sm">
        <!-- 헤더: 정렬 가능 -->
        <tr>
          <th class="p-3 select-none cursor-pointer hover:bg-gray-100" onclick={() => toggleSort('name')}>
            이름 <span class="text-gray-400 text-xs">{sortIcon('name')}</span>
          </th>
          <th class="p-3 select-none cursor-pointer hover:bg-gray-100" onclick={() => toggleSort('threads')}>
            Threads <span class="text-gray-400 text-xs">{sortIcon('threads')}</span>
          </th>
          <th class="p-3 select-none cursor-pointer hover:bg-gray-100" onclick={() => toggleSort('tier')}>
            자동 분류 <span class="text-[10px] text-gray-400 font-normal">(참고용)</span>
            <span class="text-gray-400 text-xs">{sortIcon('tier')}</span>
          </th>
          <th class="p-3 select-none cursor-pointer hover:bg-gray-100" onclick={() => toggleSort('score')}>
            점수 <span class="text-gray-400 text-xs">{sortIcon('score')}</span>
          </th>
          <th class="p-3">관심사</th>
          <th class="p-3 select-none cursor-pointer hover:bg-gray-100" onclick={() => toggleSort('submitted_at')}>
            제출 시각 <span class="text-gray-400 text-xs">{sortIcon('submitted_at')}</span>
          </th>
          <th class="p-3 select-none cursor-pointer hover:bg-gray-100" onclick={() => toggleSort('badge')}>
            뱃지 <span class="text-gray-400 text-xs">{sortIcon('badge')}</span>
          </th>
          <th class="p-3"></th>
        </tr>
        <!-- 헤더 필터: 각 컬럼별 입력 -->
        <tr class="border-t bg-gray-50/50">
          <th class="p-2">
            <input
              type="text" bind:value={fName} placeholder="이름 검색"
              class="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
          </th>
          <th class="p-2">
            <input
              type="text" bind:value={fThreads} placeholder="@ 또는 username"
              class="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
          </th>
          <th class="p-2">
            <select bind:value={activeTier}
                    class="w-full px-1 py-1 text-xs border rounded bg-white">
              <option value="all">전체</option>
              <option value="yellow">yellow</option>
              <option value="green">green</option>
              <option value="blue">blue</option>
            </select>
          </th>
          <th class="p-2">
            <div class="flex gap-1">
              <input
                type="number" bind:value={fScoreMin} placeholder="≥"
                class="w-full px-1 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
              <input
                type="number" bind:value={fScoreMax} placeholder="≤"
                class="w-full px-1 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>
          </th>
          <th class="p-2">
            <input
              type="text" bind:value={fInterests} placeholder="agent, data..."
              class="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
          </th>
          <th class="p-2">
            <div class="flex gap-1">
              <input
                type="date" bind:value={fDateFrom}
                class="w-full px-1 py-1 text-[11px] border rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
              <input
                type="date" bind:value={fDateTo}
                class="w-full px-1 py-1 text-[11px] border rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>
          </th>
          <th class="p-2">
            <input
              type="text" bind:value={fBadge} placeholder="VBS-..."
              class="w-full px-2 py-1 text-xs font-mono border rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
          </th>
          <th class="p-2"></th>
        </tr>
      </thead>
      <tbody>
        {#each filtered as r}
          <tr class="border-t hover:bg-gray-50" id="response-{r.id}">
            <td class="p-3">{r.respondent_name ?? '-'}</td>
            <td class="p-3">
              {#if r.threads_username}
                <a href="https://www.threads.net/@{r.threads_username}"
                   target="_blank" rel="noopener noreferrer"
                   class="text-blue-600 hover:underline">@{r.threads_username}</a>
              {:else}-{/if}
            </td>
            <td class="p-3">
              <span class="px-2 py-0.5 rounded text-xs border {tierColors[r.computed_tier] ?? ''}">
                {r.tier_label}
              </span>
            </td>
            <td class="p-3">{r.computed_score}</td>
            <td class="p-3 text-xs text-gray-600">{r.interests.join(', ')}</td>
            <td class="p-3 text-xs text-gray-500">
              {r.submitted_at ? new Date(r.submitted_at).toLocaleString('ko') : '-'}
            </td>
            <td class="p-3 font-mono text-xs">
              {r.respondent_badge}{r.supersedes_response_id ? ' 🔁' : ''}
            </td>
            <td class="p-3">
              <a href="/admin/community/surveys/{survey.slug}/responses/{r.id}"
                 class="text-xs text-blue-600 hover:underline">상세</a>
            </td>
          </tr>
        {/each}
        {#if filtered.length === 0}
          <tr><td colspan="8" class="p-6 text-center text-gray-500">
            조건에 맞는 응답이 없습니다.
            {#if activeFilterCount > 0}
              <button onclick={clearFilters} class="ml-2 text-blue-600 hover:underline">필터 초기화</button>
            {/if}
          </td></tr>
        {/if}
      </tbody>
    </table>
  </div>
</div>
